import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Package, LogOut, Loader2, Users, Mail, Shield, 
  Settings, UserPlus, RefreshCw, Check, X, Clock
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";  // Empty = same origin (relative URLs)

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Check admin session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/api/admin/auth/me`, {
          withCredentials: true
        });
        
        if (response.data?.ok && response.data?.user) {
          setAdminUser(response.data.user);
        } else {
          // Not authenticated - redirect to login
          navigate('/admin/login', { replace: true });
          return;
        }
      } catch (err) {
        console.error("Session check failed:", err);
        navigate('/admin/login', { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();
  }, [navigate]);

  // Load system status and invitations
  useEffect(() => {
    if (!adminUser) return;
    
    const loadData = async () => {
      try {
        const [statusRes, invitesRes] = await Promise.all([
          axios.get(`${API}/api/auth/system-status`),
          axios.get(`${API}/api/invitations`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        
        setSystemStatus(statusRes.data);
        if (invitesRes.data && Array.isArray(invitesRes.data)) {
          setInvitations(invitesRes.data);
        }
      } catch (err) {
        console.error("Failed to load admin data:", err);
      }
    };
    
    loadData();
  }, [adminUser]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/admin/auth/logout`, {}, {
        withCredentials: true
      });
      toast.success("Logged out successfully");
      navigate('/admin/login', { replace: true });
    } catch (err) {
      console.error("Logout failed:", err);
      // Clear anyway
      navigate('/admin/login', { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/95 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-orange-500" />
              <div>
                <h1 className="text-xl font-bold text-white">Admin Portal</h1>
                <p className="text-sm text-slate-400">Kanban Inventory Management</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{adminUser?.email}</p>
                <div className="flex gap-1 justify-end">
                  {adminUser?.roles?.map(role => (
                    <Badge 
                      key={role} 
                      variant="outline" 
                      className={`text-xs ${
                        role === 'owner' 
                          ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' 
                          : 'bg-orange-500/10 text-orange-400 border-orange-500/30'
                      }`}
                    >
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* System Status Card */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-500" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {systemStatus ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Backend</span>
                    {systemStatus.backend_ready ? (
                      <Badge className="bg-green-500/20 text-green-400">Online</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400">Offline</Badge>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Total Users</span>
                    <span className="text-white font-medium">{systemStatus.user_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Has Owner</span>
                    {systemStatus.has_owner ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <X className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate('/users')}
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Users
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate('/settings')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Settings
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => navigate('/items')}
              >
                <Package className="w-4 h-4 mr-2" />
                View Inventory
              </Button>
            </CardContent>
          </Card>

          {/* Recent Invitations Card */}
          <Card className="bg-slate-800 border-slate-700 md:col-span-2 lg:col-span-1">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-500" />
                Recent Invitations
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/users')}
                className="text-slate-400 hover:text-white"
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              {invitations.length > 0 ? (
                <div className="space-y-2">
                  {invitations.slice(0, 5).map(invite => (
                    <div 
                      key={invite.id} 
                      className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0"
                    >
                      <div>
                        <p className="text-sm text-white">{invite.email}</p>
                        <p className="text-xs text-slate-500">
                          {invite.roles?.join(', ') || 'viewer'}
                        </p>
                      </div>
                      <Badge 
                        variant="outline"
                        className={`text-xs ${
                          invite.status === 'PENDING' 
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                            : invite.status === 'ACCEPTED'
                            ? 'bg-green-500/10 text-green-400 border-green-500/30'
                            : 'bg-slate-500/10 text-slate-400 border-slate-500/30'
                        }`}
                      >
                        {invite.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">
                  No invitations yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admin Info Banner */}
        <Card className="mt-6 bg-slate-800/50 border-slate-700">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h3 className="font-medium text-white">Admin Portal</h3>
                <p className="text-sm text-slate-400 mt-1">
                  You are logged in to the admin portal. Your session is secured with an httpOnly cookie 
                  and will expire in 7 days. Use this portal to manage users, invitations, and system settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
